import { useFormContext } from "react-hook-form";
import { getCalculatedPricingForProduct } from "../../../utils";
import { roundToDecimalPlaces } from "../../../helpers/methods";

export const useDiscountedPrice = (fieldName, basePrice) => {
    const { watch } = useFormContext();
    const discountPercent = watch(fieldName) || 0;
    const discountAmount = basePrice * (discountPercent / 100);
    return roundToDecimalPlaces(basePrice - discountAmount, 2)
};

/**
 * Calculates min and max allowed values for different discount types.
 *
 * Discount percentage cannot exceed max discount percentage
 * Discounted listed price cannot be lower than the minimum sale price
 *
 */
export const getDiscountAllowedRanges = (product) => {
    const pricing = getCalculatedPricingForProduct(product);

    const maxDiscountPercent = Number(product.pricing.discount_max);

    const getMaxPercentage = (listed, minSale) => ((listed - minSale) / listed) * 100;
    const getMaxAmount = (effectiveMaxPercent, listed) => (effectiveMaxPercent / 100) * listed;

    // Minimum prices are independent of each other and of nominal max discount percentage
    const maxPercentByMinSalePriceGross = getMaxPercentage(
        pricing.listed.gross,
        pricing.minSale.gross,
    );
    const maxPercentByMinSalePriceNet = getMaxPercentage(pricing.listed.net, pricing.minSale.net);
    const maxPercentByMinSalePricePerSqmGross = getMaxPercentage(
        pricing.listed.gross_per_sqm,
        pricing.minSale.gross_per_sqm,
    );
    const maxPercentByMinSalePricePerSqmNet = getMaxPercentage(
        pricing.listed.net_per_sqm,
        pricing.minSale.net_per_sqm,
    );

    // The effective max discount percentage is the minimum of all calculated percentages
    const effectiveMaxDiscountPercent = Math.min(
        // NaN values occur if listed price is zero
        ...[
            maxDiscountPercent,
            maxPercentByMinSalePriceGross,
            maxPercentByMinSalePriceNet,
            maxPercentByMinSalePricePerSqmGross,
            maxPercentByMinSalePricePerSqmNet,
        ].filter((num) => !Number.isNaN(num)),
    );

    // Calculate effective maximums based on effective max discount percentage
    const maxDiscountAmountGross = getMaxAmount(effectiveMaxDiscountPercent, pricing.listed.gross);
    const maxDiscountAmountNet = getMaxAmount(effectiveMaxDiscountPercent, pricing.listed.net);
    const maxDiscountAmountPerSqmGross = getMaxAmount(
        effectiveMaxDiscountPercent,
        pricing.listed.gross_per_sqm,
    );
    const maxDiscountAmountPerSqmNet = getMaxAmount(
        effectiveMaxDiscountPercent,
        pricing.listed.net_per_sqm,
    );

    return {
        percentage: { min: -100, max: effectiveMaxDiscountPercent },
        totalAmountGross: { min: Number.MIN_SAFE_INTEGER, max: maxDiscountAmountGross },
        totalAmountNet: { min: Number.MIN_SAFE_INTEGER, max: maxDiscountAmountNet },
        perSquareMeterAmountGross: { min: Number.MIN_SAFE_INTEGER, max: maxDiscountAmountPerSqmGross },
        perSquareMeterAmountNet: { min: Number.MIN_SAFE_INTEGER, max: maxDiscountAmountPerSqmNet },
    };
};
